import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import type { ApiError } from '@/api/errors'
import type { CallerResponse, CallerStatus, CallerWriteRequest, Paginated } from '@/api/types'
import { queryKeys } from '@/api/queryKeys'
import { Card } from '@/components/Card'
import { Combobox } from '@/components/Combobox'
import { DateField } from '@/components/DateField'
import { Field } from '@/components/Field'
import { PhoneField } from '@/components/PhoneField'
import { TextArea } from '@/components/TextArea'
import { TextField } from '@/components/TextField'
import { useToast } from '@/components/Toast'
import { todayIso } from '@/lib/dates'
import { cx } from '@/lib/cx'
import { callers as api, CALLER_STATUS_META, EDITABLE_STATUSES, travel } from './api'
import { errorMessage } from '@/api/messages'

function blank(): CallerWriteRequest {
  return {
    full_name: '',
    phone: null,
    place: null,
    // The call is happening now; anything else is a correction, not the default.
    call_date: todayIso(),
    reason: null,
    requested_appointment_date: null,
    preferred_location: null,
    follow_up_date: null,
    status: 'new_call',
  }
}

function fromResponse(c: CallerResponse): CallerWriteRequest {
  return {
    full_name: c.full_name,
    phone: c.phone,
    place: c.place,
    call_date: c.call_date,
    reason: c.reason,
    requested_appointment_date: c.requested_appointment_date,
    preferred_location: c.preferred_location,
    follow_up_date: c.follow_up_date,
    status: c.status,
  }
}

/**
 * The enquiry form's state and save, shared by the dialog that logs a call and
 * the page that opens one afterwards.
 *
 * Only the name is enforced: a call half-recorded is worth more than one lost to
 * a validation error while someone is waiting on the line.
 */
export function useCallerForm({
  mode,
  callerId = '',
  onSaved,
}: {
  mode: 'create' | 'edit'
  callerId?: string
  onSaved: (caller: CallerResponse) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [form, setForm] = useState<CallerWriteRequest>(blank)
  const [error, setError] = useState<string | null>(null)

  const existing = useQuery<CallerResponse, ApiError>({
    queryKey: queryKeys.caller(callerId),
    queryFn: ({ signal }) => api.get(callerId, signal),
    enabled: mode === 'edit' && Boolean(callerId),
  })

  // Locations the doctor actually sits at, rather than a free-text guess.
  const locations = useQuery<Paginated<string>, ApiError>({
    queryKey: queryKeys.travelLocations,
    queryFn: ({ signal }) => travel.locations(signal),
  })

  useEffect(() => {
    if (existing.data) setForm(fromResponse(existing.data))
  }, [existing.data])

  const save = useMutation<CallerResponse, ApiError>({
    mutationFn: () => (mode === 'edit' ? api.update(callerId, form) : api.create(form)),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['callers'] })
      await queryClient.invalidateQueries({ queryKey: queryKeys.caller(saved.id) })
      toast.show(mode === 'edit' ? 'Enquiry updated' : 'Call logged')
      onSaved(saved)
    },
    onError: (err) => setError(errorMessage(err)),
  })

  function update(changes: Partial<CallerWriteRequest>) {
    setForm((current) => ({ ...current, ...changes }))
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    if (!form.full_name.trim()) {
      setError('A name is required.')
      return
    }
    save.mutate()
  }

  return {
    form,
    update,
    error,
    setError,
    save,
    submit,
    existing,
    locations,
    loading: existing.isLoading,
    /** A converted enquiry is kept as it was — the patient record is the live
     *  copy from then on. */
    converted: existing.data?.status === 'became_patient',
  }
}

export type CallerFormState = ReturnType<typeof useCallerForm>

function Group({
  title,
  chrome,
  children,
}: {
  title: string
  chrome: 'card' | 'plain'
  children: ReactNode
}) {
  if (chrome === 'card') return <Card title={title}>{children}</Card>
  return (
    <section className="[&>*+*]:mt-4">
      <h3 className="mb-3 text-2xs font-semibold tracking-wide text-fg-subtle uppercase">
        {title}
      </h3>
      {children}
    </section>
  )
}

export function CallerFormFields({
  state,
  chrome = 'card',
  autoFocus = false,
}: {
  state: CallerFormState
  chrome?: 'card' | 'plain'
  autoFocus?: boolean
}) {
  const { form, update, converted, locations } = state

  return (
    <>
      <Group title="Who called" chrome={chrome}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Name"
            required
            autoFocus={autoFocus}
            value={form.full_name}
            disabled={converted}
            onChange={(e) => update({ full_name: e.target.value })}
          />
          <PhoneField
            label="Phone number"
            value={form.phone ?? ''}
            disabled={converted}
            onChange={(phone) => update({ phone: phone || null })}
          />
          <TextField
            label="Place"
            value={form.place ?? ''}
            disabled={converted}
            onChange={(e) => update({ place: e.target.value || null })}
          />
          <DateField
            label="Call date"
            required
            value={form.call_date}
            max={todayIso()}
            disabled={converted}
            onChange={(iso) => update({ call_date: iso })}
          />
        </div>
      </Group>

      <Group title="What they wanted" chrome={chrome}>
        <TextArea
          label="Reason / enquiry"
          rows={3}
          maxLength={2000}
          value={form.reason ?? ''}
          disabled={converted}
          placeholder="Knee pain for two weeks, asking about an appointment."
          onChange={(e) => update({ reason: e.target.value || null })}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DateField
            label="Requested appointment date"
            value={form.requested_appointment_date ?? ''}
            disabled={converted}
            onChange={(iso) => update({ requested_appointment_date: iso || null })}
          />
          <Combobox
            label="Preferred location"
            options={locations.data?.data ?? []}
            value={form.preferred_location ?? ''}
            disabled={converted}
            onChange={(place) => update({ preferred_location: place || null })}
          />
        </div>
      </Group>

      <Group title="What happens next" chrome={chrome}>
        <DateField
          label="Follow-up date"
          hint="When someone should ring them back. This is what the Callers list chases."
          value={form.follow_up_date ?? ''}
          min={form.call_date}
          disabled={converted}
          onChange={(iso) => update({ follow_up_date: iso || null })}
        />
        <div>
          <Field label="Status">
            {() => (
              <div className="flex flex-wrap gap-2" role="group" aria-label="Status">
                {EDITABLE_STATUSES.map((value: CallerStatus) => (
                  <button
                    key={value}
                    type="button"
                    className={cx(
                      'min-h-touch rounded-md border px-4 text-sm transition-colors duration-150 ease-standard',
                      form.status === value
                        ? 'border-accent bg-accent-soft font-semibold text-accent'
                        : 'border-line-soft bg-surface text-fg-muted hover:bg-hover',
                    )}
                    aria-pressed={form.status === value}
                    disabled={converted}
                    onClick={() => update({ status: value })}
                  >
                    {CALLER_STATUS_META[value].label}
                  </button>
                ))}
              </div>
            )}
          </Field>
        </div>
      </Group>
    </>
  )
}
